<template>
  <v-row>
    <v-col class="mt-2" cols="12" offset-lg="1" lg="10">
      <v-breadcrumbs :items="breadcrumbs" divider=">" class="pl-1 pb-3" />
      <v-card>
        <v-card-title class="theme">
          <v-icon class="white--text">place</v-icon>
          <span class="headline ml-2 white--text">Add office location</span>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-form ref="form">
            <v-container>
              <v-row>
                <v-col cols="12" md="5" lg="5">
                  <v-text-field
                    label="Office name*"
                    persistent-hint
                    :rules="[validationRules.required]"
                    :disabled="tempDisable"
                    v-model="location.title"
                  />

                  <v-autocomplete
                    label="Country *"
                    persistent-hint
                    :items="countries"
                    :disabled="tempDisable"
                    item-text="name"
                    item-value="name"
                    :rules="[validationRules.required]"
                    v-model="location.country"
                  />

                  <v-text-field
                    label="Contact email"
                    persistent-hint
                    :disabled="tempDisable"
                    :rules="[validationRules.email]"
                    v-model="location.contact_email"
                  />
                  <v-row>
                    <v-col cols="12" lg="5" md="5">
                      <v-autocomplete
                        label="Country code"
                        persistent-hint
                        :items="countries"
                        :disabled="tempDisable"
                        :item-text="isdCodeName"
                        :return-object="true"
                        v-model="location.contact_isd"
                      />
                    </v-col>
                    <v-col cols="12" lg="7" md="7">
                      <v-text-field
                        label="Phone / Mobile number"
                        persistent-hint
                        type="tel"
                        :disabled="tempDisable"
                        validate-on-blur
                        :rules="[phoneValidationRule]"
                        v-model="location.contact_phone"
                      />
                    </v-col>
                  </v-row>

                  <v-text-field
                    label="Address line 1"
                    persistent-hint
                    :disabled="tempDisable"
                    v-model="location.line_1"
                  />

                  <v-text-field
                    label="Address line 2"
                    persistent-hint
                    :disabled="tempDisable"
                    v-model="location.line_2"
                  />

                  <v-text-field
                    :disabled="tempDisable"
                    label="City"
                    persistent-hint
                    :rules="[validationRules.required]"
                    v-model="location.city"
                  />

                  <v-text-field
                    :disabled="tempDisable"
                    label="State"
                    persistent-hint
                    v-model="location.state"
                  />

                  <v-text-field
                    label="Postcode"
                    :disabled="tempDisable"
                    persistent-hint
                    v-model="location.post_code"
                  />

                  <v-textarea
                    label="Additional information"
                    :disabled="tempDisable"
                    persistent-hint
                    rows="3"
                    v-model="location.description"
                  />
                </v-col>
              </v-row>
              <v-btn
                depressed
                class="save rounded-sm white--text ml-0"
                @click="save"
                >save</v-btn
              >
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
    <v-snackbar v-model="snackbar" :bottom="true" :right="true">
      {{ snackbarMessage }}
      <v-btn color="pink" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-row>
</template>
<script>
import libphonenumber from "google-libphonenumber";
import { mapGetters } from "vuex";

import { Actions } from "@/views/actions";
import { Breadcrumbs } from "@/views/breadcrumbs";
import { Component } from "@/views/component";
import { Constants } from "@/views/constants";

export default {
  name: Component.Protected.Company.Locations.Add,
  data: () => ({
    breadcrumbs: Breadcrumbs.Locations.Add,
    tempDisable: false,
    location: {
      title: "",
      type: "Office",
      city: "",
      state: "",
      line_1: "",
      line_2: "",
      country: "",
      post_code: "",
      description: "",
      contact_email: "",
      contact_phone: "",
      contact_isd: "",
    },
    phoneValidator: null,
  }),
  computed: {
    ...mapGetters(["countries", "userInfo"]),
    phoneValidationRule() {
      if (!this.location.contact_isd && !this.location.contact_phone)
        return true;
      // Both ISD and Num should be filled
      if (this.location.contact_isd && !this.location.contact_phone)
        return Constants.PhoneNumber.Messages.Error.Required;
      if (!this.location.contact_isd && this.location.contact_phone)
        return Constants.PhoneNumber.Messages.Error.SelectISD;
      if (isNaN(this.location.contact_phone))
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.location.contact_phone && this.location.contact_phone.length < 8)
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (!this.location.contact_phone || !this.location.contact_isd.code)
        return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;

      const num = this.phoneValidator.parseAndKeepRawInput(
        this.location.contact_phone,
        this.location.contact_isd.code
      );
      if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.phoneValidator.isValidNumber(num)) return true;
      return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    },
  },
  methods: {
    isdCodeName(c) {
      if (c) {
        return `${c.dial_code} - ${c.name}`;
      }
    },
    save() {
      if (this.$refs.form.validate()) {
        const data = {
          ...this.location,
          contact_isd: this.location.contact_isd
            ? this.location.contact_isd.dial_code
            : "",
        };

        this.$store
          .dispatch(Actions.LocationAdd, data)
          .then(() => this.$router.go(-1))
          .catch((e) => {
            console.error(Constants.Locations.Messages.Error.Add, e);
            this.snackbarMessage = Constants.Locations.Messages.Error.Add;
            this.snackbar = true;
          });
      }
    },
    setup() {
      if (this.userInfo.role !== Constants.Roles.HR) {
        this.$router.go(-1);
        return false;
      }
      this.phoneValidator = libphonenumber.PhoneNumberUtil.getInstance();
      this.tempDisable = false;
    },
  },
  created() {
    if (this.userInfo && this.userInfo.role) {
      this.setup();
    } else {
      this.$store.dispatch(Actions.Firebase.UserInfo).then(() => this.setup());
    }
  },
};
</script>